import React, { useRef, useEffect, useState, useCallback } from 'react';
import { BoldIcon, ItalicIcon, UnderlineIcon, ListIcon, LinkIcon, ImageIcon, BracesIcon, SparklesIcon, CheckIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { MEDIA_LIBRARY } from '../data/communications';
import { variableTokenHtml } from '../utils/variables';
import type { NoticeVariable } from '../types/variables';

/** Converts editor HTML into plain text (used for counters and previews). */
export function stripHtml(html: string): string {
  if (!html) return '';
  const el = document.createElement('div');
  el.innerHTML = html.replace(/<br\s*\/?>/gi, '\n').replace(/<\/(p|div|li)>/gi, '\n');
  return (el.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim();
}

/** True when the HTML holds any content at all — text, chip, OR an image. */
export function hasContent(html: string): boolean {
  return Boolean(stripHtml(html)) || /<img\b/i.test(html);
}
const HL_CLASS = 'mp-hl';

/** Removes the transient highlight wrappers so they never reach the state. */
export function stripHighlightHtml(html: string): string {
  if (!html || !html.includes(HL_CLASS)) return html;
  const root = document.createElement('div');
  root.innerHTML = html;
  root.querySelectorAll(`mark.${HL_CLASS}`).forEach(mark => {
    mark.replaceWith(...Array.from(mark.childNodes));
  });
  root.normalize();
  return root.innerHTML;
}

/** Wraps the first occurrence of `phrase` in a highlight mark. */
function applyHighlightHtml(html: string, phrase: string): string {
  const needle = phrase.trim();
  if (!html || needle.length < 3) return html;
  const root = document.createElement('div');
  root.innerHTML = html;

  // Progressively shorter needles, so a tip still lands when the author has
  // edited the tail of the sentence it was computed from.
  const attempts = [needle, needle.slice(0, 40), needle.slice(0, 18)].filter(a => a.trim().length >= 3);
  for (const attempt of attempts) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode() as Text | null;
    while (node) {
      const index = (node.textContent ?? '').toLowerCase().indexOf(attempt.toLowerCase());
      if (index !== -1) {
        const range = document.createRange();
        range.setStart(node, index);
        range.setEnd(node, index + attempt.length);
        const mark = document.createElement('mark');
        mark.className = HL_CLASS;
        range.surroundContents(mark);
        return root.innerHTML;
      }
      node = walker.nextNode() as Text | null;
    }
  }
  return html;
}
interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  ariaLabel: string;
  /** Enables the inline image button in the toolbar. */
  allowImages?: boolean;
  /** Drops the formatting buttons, keeping only the variable inserter. */
  variablesOnly?: boolean;
  /** Blocks line breaks — used for single-line fields like a banner title. */
  singleLine?: boolean;
  /** Variables available for insertion. Omit to hide the variable button. */
  variables?: NoticeVariable[];
  /** Text excerpt to flag visually — set when an AI tip is clicked. */
  highlight?: string | null;
  /** Shows a "generate" action in the toolbar while the field is empty. */
  onGenerate?: () => void;
}
export function RichTextEditor({
  value,
  onChange,
  placeholder,
  minHeight = 120,
  ariaLabel,
  allowImages = false,
  variablesOnly = false,
  singleLine = false,
  variables,
  highlight = null,
  onGenerate
}: RichTextEditorProps) {
  const {
    t,
    lang
  } = useLanguage();
  const editorRef = useRef<HTMLDivElement>(null);
  const savedRange = useRef<Range | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [imageOpen, setImageOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [isEmpty, setIsEmpty] = useState(!hasContent(value));

  /** Variable picker. `query` is the text typed after "{", if triggered inline. */
  const [varMenu, setVarMenu] = useState<{
    open: boolean;
    query: string;
    /** Characters to delete before inserting (the "{query}" trigger). */
    triggerLength: number;
    highlight: number;
  }>({
    open: false,
    query: '',
    triggerLength: 0,
    highlight: 0
  });
  const hasVariables = Boolean(variables && variables.length > 0);
  const matches = (variables ?? []).filter(v => v.label[lang].toLowerCase().includes(varMenu.query.toLowerCase()));

  // Sync external value in without fighting the caret while the user types.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const desired = highlight ? applyHighlightHtml(value, highlight) : value;
    if (el.innerHTML !== desired) el.innerHTML = desired;
    setIsEmpty(!hasContent(value));
  }, [value, highlight]);
  const emit = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    // Highlights are decoration only — they never make it into the content.
    const html = stripHighlightHtml(el.innerHTML);
    setIsEmpty(!hasContent(html));
    onChange(html);
  }, [onChange]);
  const closeVarMenu = useCallback(() => setVarMenu({
    open: false,
    query: '',
    triggerLength: 0,
    highlight: 0
  }), []);
  const exec = (command: string, arg?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, arg);
    emit();
  };
  const saveSelection = () => {
    const sel = window.getSelection();
    const el = editorRef.current;
    if (!sel || sel.rangeCount === 0 || !el) return;
    const range = sel.getRangeAt(0);
    if (el.contains(range.commonAncestorContainer)) savedRange.current = range.cloneRange();
  };

  /** Puts the caret back where it was before the user reached for a popover. */
  const restoreSelection = () => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (!sel) return;
    if (savedRange.current && el.contains(savedRange.current.commonAncestorContainer)) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    } else {
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  };

  // --- VARIABLES ------------------------------------------------------------

  /** Detects an inline "{query" trigger sitting just before the caret. */
  const detectTrigger = useCallback(() => {
    if (!hasVariables) return;
    const sel = window.getSelection();
    const el = editorRef.current;
    if (!sel || sel.rangeCount === 0 || !el) return;
    const range = sel.getRangeAt(0);
    if (!range.collapsed || range.startContainer.nodeType !== Node.TEXT_NODE) {
      setVarMenu(prev => prev.triggerLength > 0 ? {
        ...prev,
        open: false
      } : prev);
      return;
    }
    const text = (range.startContainer.textContent ?? '').slice(0, range.startOffset);
    const match = /\{([^{}\s]{0,30})$/.exec(text);
    if (!match) {
      setVarMenu(prev => prev.open && prev.triggerLength > 0 ? {
        open: false,
        query: '',
        triggerLength: 0,
        highlight: 0
      } : prev);
      return;
    }
    setVarMenu({
      open: true,
      query: match[1],
      triggerLength: match[0].length,
      highlight: 0
    });
  }, [hasVariables]);

  /** Inserts a chip, removing the inline trigger text when there was one. */
  const insertVariable = (variable: NoticeVariable) => {
    const el = editorRef.current;
    if (!el) return;
    const trigger = varMenu.triggerLength;
    closeVarMenu();
    restoreSelection();
    if (trigger > 0) {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        const start = Math.max(0, range.startOffset - trigger);
        const deletion = document.createRange();
        deletion.setStart(range.startContainer, start);
        deletion.setEnd(range.startContainer, range.startOffset);
        deletion.deleteContents();
        sel.removeAllRanges();
        sel.addRange(deletion);
      }
    }
    document.execCommand('insertHTML', false, `${variableTokenHtml(variable, lang)}&nbsp;`);
    savedRange.current = null;
    saveSelection();
    emit();
  };
  const openVarMenuFromToolbar = () => {
    saveSelection();
    setLinkOpen(false);
    setImageOpen(false);
    setVarMenu(prev => prev.open && prev.triggerLength === 0 ? {
      open: false,
      query: '',
      triggerLength: 0,
      highlight: 0
    } : {
      open: true,
      query: '',
      triggerLength: 0,
      highlight: 0
    });
  };

  // Close the picker when clicking elsewhere.
  useEffect(() => {
    if (!varMenu.open) return;
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (!menuRef.current?.contains(target) && !editorRef.current?.contains(target)) {
        closeVarMenu();
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [varMenu.open, closeVarMenu]);
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (varMenu.open && matches.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setVarMenu(prev => ({
          ...prev,
          highlight: (prev.highlight + 1) % matches.length
        }));
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setVarMenu(prev => ({
          ...prev,
          highlight: (prev.highlight - 1 + matches.length) % matches.length
        }));
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        insertVariable(matches[varMenu.highlight] ?? matches[0]);
        return;
      }
    }
    if (event.key === 'Escape' && varMenu.open) {
      event.preventDefault();
      // Leaves the literal "{" alone — the author may want a real brace.
      closeVarMenu();
      return;
    }
    if (singleLine && event.key === 'Enter') event.preventDefault();
  };

  // --- LINKS & IMAGES ------------------------------------------------------

  const openLink = () => {
    saveSelection();
    setImageOpen(false);
    closeVarMenu();
    setLinkUrl('');
    setLinkOpen(true);
  };
  const applyLink = () => {
    const url = linkUrl.trim();
    setLinkOpen(false);
    if (!url) return;
    restoreSelection();
    const sel = window.getSelection();
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    if (sel && sel.toString().length > 0) {
      document.execCommand('createLink', false, href);
    } else {
      document.execCommand('insertHTML', false, `<a href="${href}">${href}</a>`);
    }
    emit();
  };
  const openImage = () => {
    saveSelection();
    setLinkOpen(false);
    closeVarMenu();
    setImageUrl('');
    setImageOpen(v => !v);
  };

  /** Inserts the image where the caret sits, inside the message body. */
  const insertImage = (src: string) => {
    if (!src) return;
    setImageOpen(false);
    setImageUrl('');
    restoreSelection();
    document.execCommand('insertHTML', false, `<img src="${src}" alt="" style="max-width:100%;height:auto;border-radius:8px;margin:8px 0;" /><p><br></p>`);
    emit();
  };
  const submitImageUrl = () => {
    const url = imageUrl.trim();
    if (!url) return;
    insertImage(/^https?:\/\//i.test(url) ? url : `https://${url}`);
  };
  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = () => insertImage(String(reader.result));
      reader.readAsDataURL(file);
    });
  };
  const toolButton = (key: string, Icon: typeof BoldIcon, label: string, onClick: () => void, active = false) => <button key={key} type="button" onMouseDown={e => e.preventDefault()} onClick={onClick} title={label} aria-label={label} className={`p-1.5 rounded transition-colors ${active ? 'bg-indigo-100 text-[#4F46E5]' : 'text-slate-600 hover:bg-slate-200'}`} data-id="element-5327" data-mp="6ewyjax7sy83">
      <Icon className="w-4 h-4" data-id="element-5328" data-mp="3jvwcu202cn5j" />
    </button>;
  return <div className="relative border border-slate-200 rounded-md bg-white overflow-visible focus-within:border-[#4F46E5] focus-within:ring-1 focus-within:ring-[#4F46E5] transition-colors" data-id="element-5329" data-mp="3jul89zysnbdd">
      <div className="flex items-center gap-0.5 p-2 border-b border-slate-100 bg-slate-50 flex-wrap" data-id="element-5330" data-mp="2r6peb30lzxka">
        {!variablesOnly && <>
            {toolButton('bold', BoldIcon, t('Gras', 'Bold'), () => exec('bold'))}
            {toolButton('italic', ItalicIcon, t('Italique', 'Italic'), () => exec('italic'))}
            {toolButton('underline', UnderlineIcon, t('Souligné', 'Underline'), () => exec('underline'))}
            <span className="w-px h-4 bg-slate-300 mx-1" data-id="element-5331" data-mp="zad10yupvd20" />
            {toolButton('list', ListIcon, t('Liste à puces', 'Bulleted list'), () => exec('insertUnorderedList'))}
            {toolButton('link', LinkIcon, t('Insérer un lien', 'Insert a link'), openLink)}
            {allowImages && toolButton('image', ImageIcon, t('Insérer une image', 'Insert an image'), openImage, imageOpen)}
          </>}

        {hasVariables && <>
            {!variablesOnly && <span className="w-px h-4 bg-slate-300 mx-1" data-id="element-5332" data-mp="26oqfvm7qdvqa" />}
            <button type="button" onMouseDown={e => e.preventDefault()} onClick={openVarMenuFromToolbar} title={t('Insérer une variable', 'Insert a variable')} aria-label={t('Insérer une variable', 'Insert a variable')} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-semibold transition-colors ${varMenu.open ? 'bg-[#4F46E5] text-white' : 'text-slate-600 hover:bg-slate-200'}`} data-id="element-5333" data-mp="26rcntmyjad91">
              <BracesIcon className="w-3.5 h-3.5" data-id="element-5334" data-mp="1e25oilr99jmq" />
              {t('Variable', 'Variable')}
            </button>
          </>}

        {/* Offered only while there is nothing to overwrite. */}
        {onGenerate && isEmpty && <button type="button" onMouseDown={e => e.preventDefault()} onClick={onGenerate} className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold text-[#4F46E5] bg-white border border-[#4F46E5] hover:bg-indigo-50 transition-colors" data-id="element-5335" data-mp="3dd6qp9s9sva8">
            <SparklesIcon className="w-3.5 h-3.5" data-id="element-5336" data-mp="1bgy5lyii8mj6" />
            {t('Générer pour ce canal', 'Generate for this channel')}
          </button>}
      </div>

      {linkOpen && <div className="flex items-center gap-2 px-2 py-2 bg-indigo-50/60 border-b border-indigo-100" data-id="element-5337" data-mp="1kftdrg5406p4">
          <input type="url" autoFocus value={linkUrl} onChange={e => setLinkUrl(e.target.value)} onKeyDown={e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          applyLink();
        }
        if (e.key === 'Escape') setLinkOpen(false);
      }} placeholder={t('https://brossard.ca/avis', 'https://brossard.ca/notice')} aria-label={t('Adresse du lien', 'Link address')} className="flex-1 min-w-0 border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-[#4F46E5]" data-id="element-5338" data-mp="1gfrefdmujpd0" />
          <button type="button" onClick={applyLink} aria-label={t('Appliquer le lien', 'Apply link')} className="p-1 rounded bg-[#4F46E5] text-white hover:bg-[#4338CA] transition-colors" data-id="element-5339" data-mp="244klmtvif5uu">
            <CheckIcon className="w-3.5 h-3.5" data-id="element-5340" data-mp="2e1p7stpcv1bn" />
          </button>
          <button type="button" onClick={() => setLinkOpen(false)} aria-label={t('Annuler', 'Cancel')} className="p-1 rounded text-slate-500 hover:bg-slate-200 transition-colors" data-id="element-5341" data-mp="2j1lgh68zzgxj">
            <XIcon className="w-3.5 h-3.5" data-id="element-5342" data-mp="2rsvjdo5wspew" />
          </button>
        </div>}

      {imageOpen && allowImages && <div className="px-3 py-3 bg-indigo-50/40 border-b border-indigo-100" data-id="element-5343" data-mp="2wr5czubzjhye">
          <div className="flex items-center justify-between mb-2" data-id="element-5344" data-mp="31suofb34dxn0">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide" data-id="element-5345" data-mp="3bwjj7nkmozes">
              {t('Médiathèque municipale', 'Municipal media library')}
            </p>
            <button type="button" onClick={() => setImageOpen(false)} aria-label={t('Fermer', 'Close')} className="p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-colors" data-id="element-5346" data-mp="31y3o79ihd600">
              <XIcon className="w-3.5 h-3.5" data-id="element-5347" data-mp="3uovj83nt7olr" />
            </button>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-id="element-5348" data-mp="3po7u5nj55hu6">
            {MEDIA_LIBRARY.map(asset => <button key={asset.id} type="button" onMouseDown={e => e.preventDefault()} onClick={() => insertImage(asset.url)} className="rounded-lg overflow-hidden border border-slate-200 hover:border-[#4F46E5] transition-colors text-left bg-white" data-id="element-5349" data-mp="3kuv73hu41j3j">
                <img src={asset.url} alt={asset.label[lang]} className="w-full h-14 object-cover" data-id="element-5350" data-mp="w4wx7i8yauw2" />
                <span className="block px-1.5 py-1 text-[10px] text-slate-600 truncate" data-id="element-5351" data-mp="m70p6zbb8g26">
                  {asset.label[lang]}
                </span>
              </button>)}
          </div>

          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-indigo-100 flex-wrap" data-id="element-5352" data-mp="1evodz4v9pnkh">
            <input type="url" value={imageUrl} onChange={e => setImageUrl(e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submitImageUrl();
          }
        }} placeholder={t("Coller l'adresse d'une image…", 'Paste an image URL…')} aria-label={t("Adresse de l'image", 'Image URL')} className="flex-1 min-w-[10rem] border border-slate-200 rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus:border-[#4F46E5]" data-id="element-5353" data-mp="1jur6xqxz43c3" />
            <button type="button" onMouseDown={e => e.preventDefault()} onClick={submitImageUrl} disabled={!imageUrl.trim()} className="px-3 py-1.5 text-xs font-semibold text-white bg-[#4F46E5] rounded hover:bg-[#4338CA] transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-5354" data-mp="27qw20wh00p13">
              {t('Insérer', 'Insert')}
            </button>
            <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => fileInputRef.current?.click()} className="px-3 py-1.5 text-xs font-semibold text-[#4F46E5] bg-white border border-[#4F46E5] rounded hover:bg-indigo-50 transition-colors" data-id="element-5355" data-mp="1p1fxly270cfe">
              {t('Téléverser', 'Upload')}
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={e => {
          handleFiles(e.target.files);
          e.target.value = '';
        }} data-id="element-5356" data-mp="168lpno2zlkko" />
          </div>
        </div>}

      <div className="relative" data-id="element-5357" data-mp="5aaknok9juct">
        {isEmpty && placeholder && <span className="absolute top-3 left-3 text-sm text-slate-400 pointer-events-none select-none" data-id="element-5358" data-mp="57xsyjope5mk">
            {placeholder}
          </span>}
        <div ref={editorRef} role="textbox" aria-multiline={!singleLine} aria-label={ariaLabel} contentEditable suppressContentEditableWarning onInput={() => {
        emit();
        detectTrigger();
      }} onKeyDown={handleKeyDown} onKeyUp={() => {
        saveSelection();
        detectTrigger();
      }} onMouseUp={saveSelection} onBlur={saveSelection} style={{
        minHeight
      }} className="w-full p-3 text-sm text-slate-700 focus:outline-none [&_a]:text-[#4F46E5] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_b]:font-semibold [&_strong]:font-semibold [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg" data-id="element-5359" data-mp="eysh5tliffan" />

        {/* Variable picker */}
        {varMenu.open && hasVariables && <div ref={menuRef} role="listbox" aria-label={t('Variables disponibles', 'Available variables')} className="absolute left-3 right-3 sm:right-auto sm:w-72 top-2 z-40 bg-white rounded-lg border border-slate-200 shadow-lg overflow-hidden" data-id="element-5360" data-mp="10grmv090h9n0">
            <div className="px-3 py-2 border-b border-slate-100 bg-slate-50" data-id="element-5361" data-mp="hk9zsw53xxx3">
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide" data-id="element-5362" data-mp="hmcnh9isfeyn">
                {varMenu.query ? `${t('Recherche : ', 'Search: ')}${varMenu.query}` : t('Insérer une variable', 'Insert a variable')}
              </p>
            </div>
            {matches.length === 0 ? <p className="px-3 py-3 text-xs text-slate-400" data-id="element-5363" data-mp="3fvmzaea83m8r">
                {t('Aucune variable ne correspond. Créez-la dans le bloc Variables.', 'No matching variable. Create it in the Variables block.')}
              </p> : <ul className="max-h-56 overflow-y-auto py-1" data-id="element-5364" data-mp="7guum64qf1ah">
                {matches.map((variable, index) => <li key={variable.id} data-id="element-5365" data-mp="3fstfjr98f5v" data-mpi={index}>
                    <button type="button" role="option" aria-selected={index === varMenu.highlight} onMouseDown={e => e.preventDefault()} onMouseEnter={() => setVarMenu(prev => ({
              ...prev,
              highlight: index
            }))} onClick={() => insertVariable(variable)} className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${index === varMenu.highlight ? 'bg-indigo-50' : 'bg-white'}`} data-id="element-5366" data-mp="3gufskdq0o2w">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold ${variable.kind === 'system' ? 'bg-indigo-50 text-[#4F46E5] ring-1 ring-inset ring-[#4F46E5]/35' : 'bg-[#4F46E5] text-white'}`} data-id="element-5367" data-mp="13m4vvplo3wds">
                        {variable.label[lang]}
                      </span>
                      {variable.kind === 'custom' && <span className="text-[11px] text-slate-400 truncate" data-id="element-5368" data-mp="1rebqaj7u1tak">
                          {variable.value[lang] || t('sans valeur', 'no value')}
                        </span>}
                    </button>
                  </li>)}
              </ul>}
          </div>}
      </div>
    </div>;
}